import React from "react";
import { PerformanceBadge } from "../PerformanceBadge";
import { getBandForScore } from "../../constants/performance-bands";
import type { AwardedSupplier } from "../../types/supplier-performance.types";

interface MySuppliersTableProps {
	canCreate: boolean;
	suppliers: AwardedSupplier[];
	onRateClick: (supplier: AwardedSupplier) => void;
	onSupplierClick: (supplier: AwardedSupplier) => void;
}

const PROFILE_TYPE_NAMES: Record<number, string> = {
	1: "Client",
	2: "Head Contractor",
	3: "Subcontractor",
	4: "Consultant",
};

const formatDate = (dateString: string | null): string => {
	if (!dateString) {
		return "—";
	}
	const date = new Date(dateString);
	return date.toLocaleDateString("en-AU", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
};

export const MySuppliersTable: React.FC<MySuppliersTableProps> = ({
	suppliers,
	onSupplierClick,
	onRateClick,
	canCreate,
}) => {
	return (
		<div
			className="overflow-x-auto rounded-lg border border-gray-200 bg-white"
			data-testid="my-suppliers-table"
		>
			<table className="min-w-full divide-y divide-gray-200">
				<thead className="bg-gray-50">
					<tr>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
						>
							Supplier
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
						>
							Type
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
						>
							Engagements
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
						>
							Performance
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
						>
							Last Engaged
						</th>
						<th
							scope="col"
							className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
						>
							Actions
						</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-gray-200 bg-white">
					{suppliers.map((supplier) => {
						const band = getBandForScore(supplier.overall_average_score);
						const profileTypeName = PROFILE_TYPE_NAMES[supplier.profile_type_id] ?? "Unknown";

						return (
							<tr
								key={supplier.supplier_company_id}
								className="cursor-pointer transition-colors hover:bg-gray-50"
								onClick={() => onSupplierClick(supplier)}
								data-testid={`supplier-row-${supplier.supplier_company_id}`}
							>
								<td
									className="whitespace-nowrap px-6 py-4"
									data-testid={`supplier-name-${supplier.supplier_company_id}`}
								>
									<div className="flex items-center gap-3">
										{supplier.logo_path ? (
											<img
												src={supplier.logo_path}
												alt={`${supplier.company_name} logo`}
												className="h-8 w-8 rounded-full object-cover"
											/>
										) : (
											<div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-600">
												{supplier.company_name
													.charAt(0)
													.toUpperCase()}
											</div>
										)}
										<span className="text-sm font-medium text-gray-900">
											{supplier.company_name}
										</span>
									</div>
								</td>
								<td className="whitespace-nowrap px-6 py-4">
									<span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
										{profileTypeName}
									</span>
								</td>
								<td className="whitespace-nowrap px-6 py-4 text-center text-sm text-gray-700">
									{supplier.engagement_count}
								</td>
								<td className="whitespace-nowrap px-6 py-4">
									<PerformanceBadge
										score={supplier.overall_average_score}
										band={band}
									/>
								</td>
								<td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
									{formatDate(supplier.last_engaged_at)}
								</td>
								<td className="whitespace-nowrap px-6 py-4 text-right">
									<div className="flex items-center justify-end gap-2">
										{canCreate && (
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													onRateClick(supplier);
												}}
												className="rounded-md border border-blue-600 px-3 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
												data-testid={`rate-supplier-${supplier.supplier_company_id}`}
											>
												Rate
											</button>
										)}
									</div>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};
